<template>
  <div class="study-page">
    <div class="study-card">
      <div class="card-header">
        <h2 class="card-title">在线学习</h2>
      </div>

      <div class="progress-section">
        <div class="progress-circle-container">
          <van-circle
            v-model:current-rate="currentRate"
            :speed="100"
            :rate="currentRate"
            layer-color="#f0f0f0"
            :stroke-width="80"
          />
        </div>

        <div class="progress-info">
          <p class="progress-label">
            总学习进度：{{ detailInfo?.course?.progress || 0 }}%
          </p>
        </div>
      </div>

      <div v-if="courseInfo.totalClassHours > 0" class="plan-summary">
        <div class="plan-row">
          <span class="plan-label">计划状态</span>
          <span :class="courseInfo.planLocked ? 'plan-tag-locked' : 'plan-tag-unlocked'">{{ courseInfo.planLocked ? '已锁定' : '未锁定' }}</span>
        </div>
        <div class="plan-row">
          <span class="plan-label">总要求课时</span>
          <span class="plan-value">{{ courseInfo.totalClassHours }} h</span>
        </div>
        <div class="plan-row">
          <span class="plan-label">必修课时</span>
          <span class="plan-value">{{ courseInfo.requiredClassHours || 0 }} h</span>
        </div>
        <div v-if="courseInfo.electiveTargetClassHours > 0" class="plan-row">
          <span class="plan-label">选修还需</span>
          <span class="plan-value">{{ courseInfo.electiveTargetClassHours }} h</span>
        </div>
      </div>

      <div class="study-button-section">
        <button class="study-button" @click="goToStudy">
          {{ buttonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import { get, put } from "@/request.js";

const router = useRouter();
const props = defineProps({
  detailInfo: {
    type: Object,
    default: () => ({}),
  },
});
const currentRate = ref(props.detailInfo?.course?.progress || 0);

const courseInfo = ref({
  totalClassHours: 0,
  requiredClassHours: 0,
  electiveTargetClassHours: 0,
  planLocked: false,
});

const updateCourseInfo = () => {
  const c = props.detailInfo?.course || {};
  courseInfo.value = {
    totalClassHours: c.totalClassHours || 0,
    requiredClassHours: c.requiredClassHours || 0,
    electiveTargetClassHours: c.electiveTargetClassHours || 0,
    planLocked: c.planLocked || false,
  };
};

const buttonText = ref('去学习');
const updateButtonText = () => {
  const progress = props.detailInfo?.course?.progress || 0;
  const c = courseInfo.value;
  if (progress >= 100) {
    buttonText.value = '已完成';
  } else if (c.totalClassHours > 0 && !c.planLocked) {
    buttonText.value = '制定学习计划';
  } else {
    buttonText.value = '去学习';
  }
};

const isCoursewareDone = (courseware) =>
  !!(courseware && (courseware.isDone === true || courseware.done === true));

// 日志记录工具
const logFixProgress = (level, message, data = {}) => {
  const timestamp = new Date().toISOString();
  const logPrefix = '[课程进度修复]';
  const logData = {
    timestamp,
    level,
    message,
    ...data
  };
  
  if (level === 'error') {
    console.error(logPrefix, logData);
  } else if (level === 'warn') {
    console.warn(logPrefix, logData);
  } else {
    console.log(logPrefix, logData);
  }
};

// 兼容修复：如果progress是100%，但coursewareDone中还有课件未完成，自动修复
const fixHistoricalProgress = async () => {
  const progress = props.detailInfo?.course?.progress;
  if (!progress || progress < 100) {
    return; // 只有进度100%时才检查
  }
  
  const courseId = props.detailInfo?.courseId || props.detailInfo?.id;
  if (!courseId) {
    return;
  }
  
  logFixProgress('info', '开始检查历史进度修复', {
    courseId,
    progress,
    timestamp: new Date().toISOString()
  });
  
  try {
    // 获取课程详情，检查课件完成情况
    const res = await get(`/api/my/course/details/${courseId}`);
    if (res?.success && res.data?.coursewares) {
      const coursewares = res.data.coursewares;
      const totalCoursewares = coursewares.length;
      const completedCount = coursewares.filter(cw => isCoursewareDone(cw)).length;
      
      logFixProgress('info', '获取课程详情成功', {
        courseId,
        totalCoursewares,
        completedCount,
        backendProgress: progress
      });
      
      // 如果进度100%但还有课件未完成，自动修复
      if (completedCount < totalCoursewares) {
        logFixProgress('warn', '发现数据不一致，开始修复', {
          courseId,
          totalCoursewares,
          completedCount,
          incompleteCount: totalCoursewares - completedCount,
          backendProgress: progress
        });
        
        // 尝试从localStorage获取播放进度
        const userInfoStr = sessionStorage.getItem("userInfo");
        const userInfo = userInfoStr ? JSON.parse(userInfoStr) : {};
        const listStr = localStorage.getItem("vidoDataList");
        const list = listStr ? JSON.parse(listStr) : [];
        const videoHistoryMap = {};
        list
          .filter((el) => el.userName === userInfo.userName)
          .forEach((el) => {
            videoHistoryMap[el.activeVideoID] = el.time;
          });
        
        // 收集需要修复的课件
        const needFixCoursewares = coursewares.filter((courseware) => {
          // 检查条件：课件有视频时长，且播放进度达到100%，但后台未标记为完成
          if (
            courseware.vedio &&
            courseware.vedio.timeLength &&
            !isCoursewareDone(courseware)
          ) {
            const currentTime = videoHistoryMap[courseware.id] || 0;
            return currentTime >= courseware.vedio.timeLength;
          }
          return false;
        });
        
        logFixProgress('info', '识别需要修复的课件', {
          courseId,
          needFixCount: needFixCoursewares.length,
          needFixCoursewares: needFixCoursewares.map(cw => ({
            id: cw.id,
            title: cw.title,
            timeLength: cw.vedio?.timeLength,
            currentTime: videoHistoryMap[cw.id] || 0
          }))
        });
        
        if (needFixCoursewares.length === 0) {
          logFixProgress('info', '未发现需要修复的课件', {
            courseId,
            reason: '所有未完成的课件播放进度都未达到100%'
          });
          return;
        }
        
        // 为每个需要修复的课件单独计算进度并修复
        const fixResults = [];
        for (let index = 0; index < needFixCoursewares.length; index++) {
          const courseware = needFixCoursewares[index];
          // 计算修复后的完成数量：已完成数量 + 当前修复的课件数量
          const fixedCompletedCount = completedCount + index + 1;
          const learningProgress = totalCoursewares > 0
            ? ((fixedCompletedCount / totalCoursewares) * 100).toFixed(2)
            : 100;
          
          // 获取课件的播放进度作为 currentTime
          const currentTime = videoHistoryMap[courseware.id] || courseware.vedio?.timeLength || 0;
          
          const fixStartTime = Date.now();
          try {
            // 静默修复，传递 currentTime 确保后端能获取最新进度
            await put(`/api/my/course/coursewareDone/${courseId}/${courseware.id}?progress=${learningProgress}&completed=true&currentTime=${currentTime}`);
            const fixDuration = Date.now() - fixStartTime;
            fixResults.push({
              coursewareId: courseware.id,
              coursewareTitle: courseware.title,
              status: 'success',
              learningProgress,
              duration: fixDuration
            });
            logFixProgress('info', '课件修复成功', {
              courseId,
              coursewareId: courseware.id,
              coursewareTitle: courseware.title,
              learningProgress,
              duration: `${fixDuration}ms`
            });
          } catch (error) {
            const fixDuration = Date.now() - fixStartTime;
            fixResults.push({
              coursewareId: courseware.id,
              coursewareTitle: courseware.title,
              status: 'failed',
              error: error?.message || '未知错误',
              duration: fixDuration
            });
            logFixProgress('error', '课件修复失败', {
              courseId,
              coursewareId: courseware.id,
              coursewareTitle: courseware.title,
              error: error?.message || error,
              duration: `${fixDuration}ms`
            });
          }
        }
        
        // 汇总修复结果
        const successCount = fixResults.filter(r => r.status === 'success').length;
        const failedCount = fixResults.filter(r => r.status === 'failed').length;
        logFixProgress('info', '修复完成', {
          courseId,
          totalFixed: needFixCoursewares.length,
          successCount,
          failedCount,
          results: fixResults
        });
      } else {
        logFixProgress('info', '数据一致，无需修复', {
          courseId,
          totalCoursewares,
          completedCount,
          backendProgress: progress
        });
      }
    } else {
      logFixProgress('warn', '获取课程详情失败或数据格式异常', {
        courseId,
        response: res
      });
    }
  } catch (error) {
    // 静默失败，不影响用户体验
    logFixProgress('error', '修复历史进度失败', {
      courseId,
      error: error?.message || error,
      stack: error?.stack
    });
  }
};

watch(() => props.detailInfo, () => {
  currentRate.value = props.detailInfo?.course?.progress || 0;
  updateCourseInfo();
  updateButtonText();
  fixHistoricalProgress();
}, { immediate: true, deep: true });

// 方法
const goToStudy = () => {
  router.push({
    path: "/mobileCurriculum",
    query: { id: props.detailInfo.id, courseId: props.detailInfo.courseId },
  });
};
</script>

<style scoped lang="less">
.study-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 24px 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  text-align: center;
}

.card-header {
  margin-bottom: 30px;
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 16px;

  .card-title {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
    text-align: left;
  }
}

.progress-section {
  margin-bottom: 40px;
}

.progress-circle-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
}

.progress-info {
  .progress-label {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
    font-weight: 500;
  }
}

.plan-summary {
  margin-bottom: 24px;
  padding: 12px 16px;
  background: #f7f8fa;
  border-radius: 8px;
  .plan-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
    font-size: 13px;
    .plan-label {
      color: #909399;
    }
    .plan-value {
      color: #303133;
      font-weight: 500;
    }
    .plan-tag-locked {
      color: #fff;
      background: #67c23a;
      padding: 1px 8px;
      border-radius: 4px;
      font-size: 12px;
    }
    .plan-tag-unlocked {
      color: #fff;
      background: #e6a23c;
      padding: 1px 8px;
      border-radius: 4px;
      font-size: 12px;
    }
  }
}

.study-button-section {
  .study-button {
    width: 200px;
    height: 50px;
    background: linear-gradient(135deg, #1890ff, #36cfc9);
    color: white;
    border: none;
    border-radius: 25px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
}
</style>
